import { useMemo, useRef, useEffect, useState, useCallback } from 'react';
// Migrated v7.4.0 from `react-quill@2.0.0` (depended on quill@1.x with the
// XSS in GHSA-4943-9vgg-gr5r) to `react-quill-new@3.x` — the actively
// maintained fork that targets quill@2.x where the XSS was patched. Same
// API + same CSS path inside the package, so this is a one-line swap.
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { FolderOpen, Plus, Trash2, X, Check } from 'lucide-react';

// ============================================
// REGISTER QUILL FORMATS
// ============================================

// Extend Quill's whitelist for colors
const _ColorClass = Quill.import('attributors/class/color');
const ColorStyle = Quill.import('attributors/style/color');
const BackgroundStyle = Quill.import('attributors/style/background');

// Add our custom colors to the whitelist
ColorStyle.whitelist = [
  '#22c55e', '#ef4444', '#3b82f6', '#fbbf24', '#ffffff', '#9ca3af', '#60a5fa',
  'rgb(34, 197, 94)', 'rgb(239, 68, 68)', 'rgb(59, 130, 246)'
];
BackgroundStyle.whitelist = [
  'rgba(34, 197, 94, 0.25)', 'rgba(239, 68, 68, 0.25)', 'rgba(59, 130, 246, 0.3)',
  '#1a2e1a', '#2e1a1a', '#1a1a2e'
];

Quill.register(ColorStyle, true);
Quill.register(BackgroundStyle, true);

// ============================================
// TEMPLATE MANAGEMENT
// ============================================

const getTemplates = (templateType) => {
  try {
    const stored = localStorage.getItem(`journifi_templates_${templateType}`);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveTemplates = (templateType, templates) => {
  localStorage.setItem(`journifi_templates_${templateType}`, JSON.stringify(templates));
};

// ============================================
// SECTION INPUT MODAL
// ============================================

const SectionInputModal = ({ isOpen, onClose, onSubmit, darkMode }) => {
  const [sectionName, setSectionName] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    if (!isOpen) {
      setSectionName('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (sectionName.trim()) {
      onSubmit(sectionName.trim());
      setSectionName('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-9999" 
      onClick={onClose}
    >
      <div 
        className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-5 w-80 shadow-2xl`}
        onClick={e => e.stopPropagation()}
      >
        <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Section Header
        </h3>
        <input
          ref={inputRef}
          type="text"
          value={sectionName}
          onChange={(e) => setSectionName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
            if (e.key === 'Escape') onClose();
          }}
          placeholder="Enter section name..."
          className="w-full input-modern mb-3"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            type="button"
            className={`px-3 py-1.5 rounded-lg text-sm ${
              darkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            type="button"
            className="px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// TEMPLATE MODAL
// ============================================

const TemplateModal = ({ 
  isOpen, 
  onClose, 
  templates, 
  onLoadTemplate, 
  onSaveTemplate, 
  onDeleteTemplate,
  darkMode,
  currentContent
}) => {
  const [newTemplateName, setNewTemplateName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  if (!isOpen) return null;

  const handleSave = () => {
    if (newTemplateName.trim()) {
      onSaveTemplate(newTemplateName.trim(), currentContent);
      setNewTemplateName('');
      setShowSaveInput(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-9999" onClick={onClose}>
      <div 
        className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 w-96 max-h-[80vh] overflow-hidden flex flex-col shadow-2xl`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Templates
          </h3>
          <button onClick={onClose} type="button" className={`p-1 rounded-sm ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
            <X size={20} className={darkMode ? 'text-gray-400' : 'text-gray-600'} />
          </button>
        </div>

        <div className="mb-4">
          {showSaveInput ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Template name..."
                className={`flex-1 px-3 py-2 rounded-lg border ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                autoFocus
              />
              <button onClick={handleSave} type="button" className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <Check size={18} />
              </button>
              <button onClick={() => setShowSaveInput(false)} type="button" className={`px-3 py-2 rounded-lg ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                <X size={18} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowSaveInput(true)}
              type="button"
              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg ${
                darkMode 
                  ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30 border border-green-600/30' 
                  : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
              }`}
            >
              <Plus size={18} />
              Save Current as Template
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {templates.length === 0 ? (
            <p className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              No templates saved yet
            </p>
          ) : (
            <div className="space-y-2">
              {templates.map((template, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    darkMode ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <button
                    onClick={() => {
                      onLoadTemplate(template.content);
                      onClose();
                    }}
                    type="button"
                    className={`flex-1 text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  >
                    {template.name}
                  </button>
                  <button
                    onClick={() => onDeleteTemplate(index)}
                    type="button"
                    className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-sm"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN RICH TEXT EDITOR COMPONENT
// ============================================

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder, 
  darkMode = false,
  minHeight = '120px',
  templateType = 'default'
}) => {
  const quillRef = useRef(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [pendingSectionIndex, setPendingSectionIndex] = useState(null);

  useEffect(() => {
    setTemplates(getTemplates(templateType));
  }, [templateType]);

  // Bullish format - green text on green background
  const applyBullishFormat = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      if (range && range.length > 0) {
        quill.formatText(range.index, range.length, {
          'color': '#22c55e',
          'background': 'rgba(34, 197, 94, 0.25)'
        }, 'user');
      }
    }
  }, []);

  // Bearish format - red text on red background
  const applyBearishFormat = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      if (range && range.length > 0) {
        quill.formatText(range.index, range.length, {
          'color': '#ef4444',
          'background': 'rgba(239, 68, 68, 0.25)'
        }, 'user');
      }
    }
  }, []);

  // Insert horizontal divider
  const insertDivider = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      // Insert a styled line using blockquote with special marker
      quill.insertText(range.index, '\n');
      quill.insertText(range.index + 1, '─────────────────────────────────');
      quill.formatLine(range.index + 1, 1, { 'align': 'center' });
      quill.formatText(range.index + 1, 33, { 'color': '#4B5563' });
      quill.insertText(range.index + 34, '\n');
      quill.setSelection(range.index + 35);
    }
  }, []);

  // Open section modal
  const openSectionModal = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      setPendingSectionIndex(range.index);
      setShowSectionModal(true);
    }
  }, []);

  // Insert section header
  const insertSectionHeader = useCallback((sectionName) => {
    const quill = quillRef.current?.getEditor();
    if (quill && pendingSectionIndex !== null) {
      const text = `▎ ${sectionName.toUpperCase()}`;
      quill.insertText(pendingSectionIndex, '\n');
      quill.insertText(pendingSectionIndex + 1, text);
      quill.formatText(pendingSectionIndex + 1, text.length, {
        'color': '#60a5fa',
        'bold': true
      }, 'user');
      quill.insertText(pendingSectionIndex + 1 + text.length, '\n');
      quill.setSelection(pendingSectionIndex + text.length + 2);
      setPendingSectionIndex(null);
    }
  }, [pendingSectionIndex]);

  // Template handlers
  const handleSaveTemplate = (name, content) => {
    const newTemplates = [...templates, { name, content, createdAt: Date.now() }];
    setTemplates(newTemplates);
    saveTemplates(templateType, newTemplates);
  };

  const handleDeleteTemplate = (index) => {
    const newTemplates = templates.filter((_, i) => i !== index);
    setTemplates(newTemplates);
    saveTemplates(templateType, newTemplates);
  };

  const handleLoadTemplate = (content) => {
    onChange(content);
  };

  // Quill modules
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'header': [1, 2, 3, false] }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['bullish', 'bearish', 'divider', 'section'],
        ['clean']
      ],
      handlers: {
        'bullish': () => {},
        'bearish': () => {},
        'divider': () => {},
        'section': () => {}
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  // Set up handlers after mount
  useEffect(() => {
    if (quillRef.current) {
      const toolbar = quillRef.current.getEditor().getModule('toolbar');
      toolbar.addHandler('bullish', applyBullishFormat);
      toolbar.addHandler('bearish', applyBearishFormat);
      toolbar.addHandler('divider', insertDivider);
      toolbar.addHandler('section', openSectionModal);
    }
  }, [applyBullishFormat, applyBearishFormat, insertDivider, openSectionModal]);

  // Quill 2.x / react-quill-new dropped 'bullet' as a separate format —
  // bullet lists are now expressed via the 'list' format with value 'bullet'
  // (vs 'ordered'). Including 'bullet' here threw a console error on every
  // editor mount: "Cannot register 'bullet' specified in 'formats' config."
  // The toolbar's bullet button still works because the toolbar config
  // uses `{ list: 'bullet' }` — not a separate format identifier.
  const formats = [
    'bold', 'italic', 'underline', 'strike',
    'list',
    'header',
    'align',
    'blockquote', 'code-block',
    'color', 'background'
  ];

  return (
    <div className={`rich-text-editor ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      <style>{`
        .rich-text-editor .ql-container {
          min-height: ${minHeight};
          font-size: 14px;
          font-family: inherit;
        }
        
        .rich-text-editor .ql-editor {
          min-height: ${minHeight};
          padding: 12px;
          text-align: left;
        }
        
        .rich-text-editor .ql-editor.ql-blank::before {
          font-style: italic;
          left: 12px;
        }
        
        /* Dark mode */
        .rich-text-editor.dark-mode .ql-toolbar {
          background: #374151;
          border-color: #4B5563;
          border-top: none;
          flex-wrap: wrap;
        }
        
        .rich-text-editor.dark-mode .ql-container {
          background: #374151;
          border-color: #4B5563;
          border-radius: 0 0 8px 8px;
          color: #E5E7EB;
        }
        
        .rich-text-editor.dark-mode .ql-editor.ql-blank::before {
          color: #6B7280;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button {
          color: #9CA3AF;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button:hover {
          color: #E5E7EB;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button.ql-active {
          color: #3B82F6;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar .ql-stroke {
          stroke: #9CA3AF;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button:hover .ql-stroke {
          stroke: #E5E7EB;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button.ql-active .ql-stroke {
          stroke: #3B82F6;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar .ql-fill {
          fill: #9CA3AF;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button:hover .ql-fill {
          fill: #E5E7EB;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button.ql-active .ql-fill {
          fill: #3B82F6;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar .ql-picker {
          color: #9CA3AF;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar .ql-picker-label:hover {
          color: #E5E7EB;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar .ql-picker-options {
          background: #1F2937;
          border-color: #4B5563;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar .ql-picker-item:hover {
          color: #E5E7EB;
        }
        
        .rich-text-editor.dark-mode .ql-editor blockquote {
          border-left: 4px solid #3B82F6;
          padding-left: 12px;
          color: #9CA3AF;
          background: rgba(59, 130, 246, 0.1);
          margin: 8px 0;
          border-radius: 0 4px 4px 0;
        }
        
        /* Code Block */
        .rich-text-editor.dark-mode .ql-editor pre.ql-syntax,
        .rich-text-editor.dark-mode .ql-editor .ql-code-block-container,
        .rich-text-editor.dark-mode .ql-editor pre {
          background: rgba(0, 0, 0, 0.4) !important;
          color: #10B981 !important;
          border-radius: 8px;
          padding: 16px !important;
          margin: 8px 0;
          font-family: 'Consolas', 'Monaco', 'Fira Code', monospace !important;
          font-size: 13px;
          border: 1px solid rgba(75, 85, 99, 0.5);
          white-space: pre-wrap;
          overflow-x: auto;
        }
        
        /* Custom buttons */
        .rich-text-editor .ql-toolbar .ql-bullish,
        .rich-text-editor .ql-toolbar .ql-bearish,
        .rich-text-editor .ql-toolbar .ql-divider,
        .rich-text-editor .ql-toolbar .ql-section {
          width: auto !important;
          padding: 3px 6px !important;
        }
        
        .rich-text-editor .ql-toolbar .ql-bullish::before {
          content: '📈';
          font-size: 14px;
        }
        
        .rich-text-editor .ql-toolbar .ql-bearish::before {
          content: '📉';
          font-size: 14px;
        }
        
        .rich-text-editor .ql-toolbar .ql-divider::before {
          content: '—';
          font-size: 16px;
          font-weight: bold;
        }
        
        .rich-text-editor .ql-toolbar .ql-section::before {
          content: '§';
          font-size: 16px;
          font-weight: bold;
        }
        
        .rich-text-editor .ql-toolbar button.ql-bullish:hover::before {
          filter: brightness(1.5);
        }
        
        .rich-text-editor .ql-toolbar button.ql-bearish:hover::before {
          filter: brightness(1.5);
        }
        
        .rich-text-editor .ql-toolbar button.ql-divider:hover,
        .rich-text-editor .ql-toolbar button.ql-section:hover {
          color: #3B82F6 !important;
        }
        
        /* Light mode */
        .rich-text-editor.light-mode .ql-toolbar {
          background: #F9FAFB;
          border-color: #D1D5DB;
          border-top: none;
        }
        
        .rich-text-editor.light-mode .ql-container {
          background: #FFFFFF;
          border-color: #D1D5DB;
          border-radius: 0 0 8px 8px;
          color: #111827;
        }
        
        .rich-text-editor.light-mode .ql-editor.ql-blank::before {
          color: #9CA3AF;
        }
        
        .rich-text-editor.light-mode .ql-editor blockquote {
          border-left: 4px solid #3B82F6;
          padding-left: 12px;
          color: #6B7280;
          background: rgba(59, 130, 246, 0.05);
        }
        
        .rich-text-editor.light-mode .ql-editor pre.ql-syntax,
        .rich-text-editor.light-mode .ql-editor .ql-code-block-container,
        .rich-text-editor.light-mode .ql-editor pre {
          background: #F3F4F6 !important;
          color: #059669 !important;
          border-radius: 8px;
          padding: 16px !important;
          margin: 8px 0;
          font-family: 'Consolas', 'Monaco', 'Fira Code', monospace !important;
          border: 1px solid #E5E7EB;
        }
        
        /* Focus */
        .rich-text-editor .ql-container:focus-within {
          border-color: #3B82F6;
        }
        
        /* Lists */
        .rich-text-editor .ql-editor ol,
        .rich-text-editor .ql-editor ul {
          padding-left: 1.5em;
        }
        
        /* Headers */
        .rich-text-editor.dark-mode .ql-editor h1,
        .rich-text-editor.dark-mode .ql-editor h2,
        .rich-text-editor.dark-mode .ql-editor h3 {
          color: #F3F4F6;
        }
        
        /* Template toolbar */
        .rich-text-editor .template-toolbar {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 8px 12px;
          border: 1px solid;
          border-bottom: none;
        }
        
        .rich-text-editor.dark-mode .template-toolbar {
          background: #374151;
          border-color: #4B5563;
          border-radius: 8px 8px 0 0;
        }
        
        .rich-text-editor.light-mode .template-toolbar {
          background: #F9FAFB;
          border-color: #D1D5DB;
          border-radius: 8px 8px 0 0;
        }
        
        .template-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 12px;
          transition: all 0.15s;
          border: none;
          cursor: pointer;
        }
        
        .rich-text-editor.dark-mode .template-btn {
          color: #9CA3AF;
          background: transparent;
        }
        
        .rich-text-editor.dark-mode .template-btn:hover {
          color: #E5E7EB;
          background: rgba(255,255,255,0.1);
        }
        
        .rich-text-editor.light-mode .template-btn {
          color: #6B7280;
          background: transparent;
        }
        
        .rich-text-editor.light-mode .template-btn:hover {
          color: #374151;
          background: rgba(0,0,0,0.05);
        }
      `}</style>
      
      <div className="template-toolbar">
        <button 
          className="template-btn"
          onClick={() => setShowTemplateModal(true)}
          title="Templates"
          type="button"
        >
          <FolderOpen size={14} />
          Templates
        </button>
      </div>
      
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
      
      <TemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        templates={templates}
        onLoadTemplate={handleLoadTemplate}
        onSaveTemplate={handleSaveTemplate}
        onDeleteTemplate={handleDeleteTemplate}
        darkMode={darkMode}
        currentContent={value}
      />
      
      <SectionInputModal
        isOpen={showSectionModal}
        onClose={() => {
          setShowSectionModal(false);
          setPendingSectionIndex(null);
        }}
        onSubmit={insertSectionHeader}
        darkMode={darkMode}
      />
    </div>
  );
};

export default RichTextEditor;
